/**
 * <s-recently-viewed> — Remembers product pages a visitor opens (this browser only)
 * and shows up to four other ones as cards. Stays hidden until it has at least one.
 *
 * Required markup / data:
 *   data-product-url (on self)  — current product URL from Liquid (product.url).
 *   data-currency, data-locale  — used to format prices from the product JSON.
 *   template[data-card-template] — one card; slots below are filled per product.
 *     [data-slot="link"]  (href), [data-slot="image"] (img), [data-slot="title"],
 *     [data-slot="price"], [data-slot="compare"] (shown only when higher than price),
 *     [data-slot="sold-out"] (shown when unavailable), [data-slot="view"] (href + aria-label suffix).
 *   [data-recent-list] — where cards are appended.
 *
 * Product data comes from the Ajax product endpoint: the stored product URL + ".js".
 */
const STORAGE_KEY = 'nicks-vtg:recently-viewed';
const MAX_STORED = 8;
const MAX_SHOWN = 4;

class SRecentlyViewed extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const current = this.dataset.productUrl;
    const stored = this.#read();
    const others = stored.filter((url) => url !== current).slice(0, MAX_SHOWN);

    if (current) this.#write([current, ...others, ...stored.filter((url) => url !== current && !others.includes(url))]);
    if (others.length) this.#render(others);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #read() {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value.filter((url) => typeof url === 'string') : [];
    } catch {
      return [];
    }
  }

  #write(urls) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(urls.slice(0, MAX_STORED)));
    } catch {
      // Storage can be unavailable (private mode); the row simply stays hidden.
    }
  }

  async #render(urls) {
    const template = this.querySelector('template[data-card-template]');
    const list = this.querySelector('[data-recent-list]');
    if (!template || !list) return;

    const results = await Promise.all(urls.map(async (url) => {
      try {
        const response = await fetch(`${url}.js`, { signal: this.#ac.signal, headers: { Accept: 'application/json' } });
        return response.ok ? await response.json() : null;
      } catch {
        return null;
      }
    }));

    const products = results.filter(Boolean);
    if (!products.length || this.#ac.signal.aborted) return;

    // Drop products that no longer exist from storage.
    this.#write([this.dataset.productUrl, ...products.map((product) => product.url)].filter(Boolean));

    const money = new Intl.NumberFormat(this.dataset.locale || undefined, { style: 'currency', currency: this.dataset.currency || 'USD' });

    for (const product of products) {
      const card = template.content.firstElementChild.cloneNode(true);
      const slot = (name) => card.querySelector(`[data-slot="${name}"]`);

      slot('link')?.setAttribute('href', product.url);
      const image = slot('image');
      if (image && product.featured_image) {
        const src = new URL(product.featured_image, location.href);
        src.searchParams.set('width', '600');
        image.src = src.toString();
        image.alt = product.title;
      }
      const title = slot('title');
      if (title) title.textContent = product.title;
      const price = slot('price');
      if (price) price.textContent = money.format(product.price / 100);
      const compare = slot('compare');
      if (compare) {
        if (product.compare_at_price && product.compare_at_price > product.price) {
          compare.textContent = money.format(product.compare_at_price / 100);
          compare.hidden = false;
        } else {
          compare.remove();
        }
      }
      const soldOut = slot('sold-out');
      if (soldOut) soldOut.hidden = Boolean(product.available);
      const view = slot('view');
      if (view) {
        view.setAttribute('href', product.url);
        view.setAttribute('aria-label', `${view.textContent.trim()}: ${product.title}`);
      }
      list.append(card);
    }

    this.hidden = false;
  }
}

if (!customElements.get('s-recently-viewed')) {
  customElements.define('s-recently-viewed', SRecentlyViewed);
}
